import {
  useId,
  useMemo,
  useState,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from './cx';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean | undefined;
  ref?: Ref<HTMLTextAreaElement> | undefined;
}

export function Textarea({ invalid, className, rows = 3, ...rest }: TextareaProps) {
  return (
    <textarea
      className={cx('eb-input eb-textarea', className)}
      rows={rows}
      aria-invalid={invalid ? true : undefined}
      {...rest}
    />
  );
}

export interface CheckboxProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: ReactNode | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
  'aria-label'?: string | undefined;
  className?: string | undefined;
}

/** A tick box with its label beside it. The whole label is clickable. */
export function Checkbox({
  label,
  checked,
  onChange,
  hint,
  disabled,
  id,
  className,
  ...aria
}: CheckboxProps) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div className={cx('eb-check', className)}>
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-label={aria['aria-label']}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={inputId}>
        <span>{label}</span>
        {hint ? <span className="eb-check__hint">{hint}</span> : null}
      </label>
    </div>
  );
}

export interface ToggleProps {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: ReactNode | undefined;
  disabled?: boolean | undefined;
  id?: string | undefined;
}

/** An on/off switch (a checkbox underneath, so keyboard and screen readers work as usual). */
export function Toggle({ label, checked, onChange, hint, disabled, id }: ToggleProps) {
  const auto = useId();
  const inputId = id ?? auto;
  return (
    <div className="eb-toggle">
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <label htmlFor={inputId}>
        <span className="eb-toggle__track" aria-hidden="true">
          <span className="eb-toggle__knob" />
        </span>
        <span>
          {label}
          {hint ? <span className="eb-check__hint">{hint}</span> : null}
        </span>
      </label>
    </div>
  );
}

export interface MultiSelectOption {
  value: string;
  label: string;
  hint?: string | undefined;
}

export interface MultiSelectProps {
  options: readonly MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  label: string;
  disabled?: boolean | undefined;
  /** Show a search box when there are more options than this. Default 8. */
  searchAbove?: number | undefined;
  emptyText?: string | undefined;
  invalid?: boolean | undefined;
}

/** Pick several from a list by ticking boxes, with "all" and "none" shortcuts. */
export function MultiSelect({
  options,
  value,
  onChange,
  label,
  disabled,
  searchAbove = 8,
  emptyText = 'Nothing to pick yet.',
  invalid,
}: MultiSelectProps) {
  const [search, setSearch] = useState('');
  const selected = useMemo(() => new Set(value), [value]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? options.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(q)) : options;
  }, [options, search]);

  const toggle = (v: string, on: boolean): void => {
    const next = new Set(value);
    if (on) next.add(v);
    else next.delete(v);
    const known = new Set(options.map((o) => o.value));
    onChange([
      ...options.map((o) => o.value).filter((x) => next.has(x)),
      ...[...next].filter((x) => !known.has(x)),
    ]);
  };

  return (
    <div className="eb-multi" role="group" aria-label={label} data-invalid={invalid || undefined}>
      {options.length > searchAbove ? (
        <input
          className="eb-input"
          type="search"
          placeholder="Search…"
          aria-label={`Search ${label}`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      ) : null}
      {options.length > 1 && !disabled ? (
        <div className="eb-multi__bar">
          <button
            type="button"
            className="eb-linkbtn"
            onClick={() => onChange([...new Set([...value, ...shown.map((o) => o.value)])])}
          >
            Select all{search ? ' shown' : ''}
          </button>
          <button
            type="button"
            className="eb-linkbtn"
            onClick={() => onChange(value.filter((v) => !shown.some((o) => o.value === v)))}
          >
            Clear{search ? ' shown' : ''}
          </button>
          <span className="eb-multi__count">{value.length} selected</span>
        </div>
      ) : null}
      <div className="eb-multi__list">
        {shown.length === 0 ? (
          <p className="eb-muted">{options.length === 0 ? emptyText : 'No match.'}</p>
        ) : null}
        {shown.map((o) => (
          <Checkbox
            key={o.value}
            label={o.label}
            hint={o.hint}
            checked={selected.has(o.value)}
            disabled={disabled}
            onChange={(on) => toggle(o.value, on)}
          />
        ))}
      </div>
    </div>
  );
}
